import { Injectable, Injector } from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { getAppCheckToken } from '../app-check';

/**
 * Para llamadas públicas a nuestro backend que no pueden esperar al primer
 * token de App Check (reCAPTCHA tarda ~1,5 s en darlo al cargar la página):
 * salen sin la cabecera X-Firebase-AppCheck. Uso:
 * `this.http.get(url, { context: withoutAppCheck() })`.
 */
export const SKIP_APP_CHECK = new HttpContextToken<boolean>(() => false);

export function withoutAppCheck(context = new HttpContext()): HttpContext {
  return context.set(SKIP_APP_CHECK, true);
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = localStorage.getItem('authToken');
    const router = this.injector.get(Router);
    const currentUrl = router.url;

    // 1. Check if we are in the target views
    const isTargetView = currentUrl.includes('/admin/footPrint') || currentUrl.includes('/admin/travelReview');
    const isRawDataView = currentUrl.includes('/admin/footPrint/raw-data');

    // 2. Strict Whitelist of audited Predictland endpoints
    const workEndpointsWhitelist = [
      '/api/backoffice/footprint/users',
      '/api/backoffice/users',
      '/api/backoffice/footprint/segment',
      '/api/backoffice/footprint/user/',
      '/api/backoffice/footprint-segment/',
      '/api/backoffice/footprint/tag'
    ];

    // 3. Check if current request matches the whitelist
    const matchesWhitelist = workEndpointsWhitelist.some(endpoint => req.url.includes(endpoint));

    // 4. ENVIRONMENT CHECK: Only rewrite if environment is configured for the work schema
    const isWorkEnvironment = environment.footprintsSchema === 'work';

    let requestToForward = req;

    // 5. Rewrite URL if ALL conditions are met
    if (isTargetView && !isRawDataView && matchesWhitelist && isWorkEnvironment) {
      const rewrittenUrl = req.url.replace('/api/backoffice/', '/api/backoffice/work/');
      requestToForward = req.clone({ url: rewrittenUrl });
    }

    // 6. Handle Authorization header
    if (token) {
      requestToForward = requestToForward.clone({
        setHeaders: { Authorization: `Bearer ${token}` }
      });
    }

    // 7. Cabecera de App Check. Solo hacia nuestro backend: el token acredita a
    //    esta app ante Firebase y no debe viajar a terceros. Si todavía no hay
    //    token (entorno sin site key, o App Check aún inicializándose) la
    //    petición sale igual y es el backend quien decide si la acepta.
    if (!targetsOwnApi(requestToForward.url) || req.context.get(SKIP_APP_CHECK)) {
      return next.handle(requestToForward);
    }

    return from(getAppCheckToken()).pipe(
      switchMap((appCheckToken) =>
        next.handle(
          appCheckToken
            ? requestToForward.clone({
                setHeaders: { 'X-Firebase-AppCheck': appCheckToken },
              })
            : requestToForward,
        ),
      ),
    );
  }
}

/**
 * Todas las llamadas del backoffice cuelgan de environment.laravelApi o de
 * laravelBackoffice, que es un prefijo del primero en los cuatro entornos.
 * Quedan fuera los assets servidos por HttpClient (traducciones, SVG inline),
 * que no necesitan la cabecera.
 */
function targetsOwnApi(url: string): boolean {
  return [environment.laravelApi, environment.laravelBackoffice].some(
    (base) => !!base && url.startsWith(base),
  );
}